// Updated useSplit hook with caching functionality
'use client';
import { useRef, useState, useEffect, useCallback } from 'react';
import { SplitFactory } from '@splitsoftware/splitio';
import { SPLIT_API_KEY } from '../constants/app';
import { canUseWindow } from '../wag-react/common/xenv/core';
import { useSegmentEvent } from './useSegmentEvent';
import { handleSplitTreatment } from '../utils/split/handleSplitTreatment';

let factory: SplitIO.ISDK;
let client: SplitIO.IClient;

export const useSplit = (FeatureFlag: string) => {
  const splitClientRef = useRef(false);
  const [isSegmentReady, setIsSegmentReady] = useState(false);
  const [splitTreatment, setSplitTreatment] = useState(null);
  const { trackEvent } = useSegmentEvent();

  useEffect(() => {
    if (window.analytics) {
      window.analytics.ready(() => {
        setIsSegmentReady(true);
      });
    } else {
      const onSegmentLoad = () => {
        window.analytics.ready(() => {
          setIsSegmentReady(true);
        });
      };
      window.addEventListener('segmentLoaded', onSegmentLoad);
      return () => window.removeEventListener('segmentLoaded', onSegmentLoad);
    }
  }, []);

  const getKey = () => {
    if (!canUseWindow) return null;

    if (window.analytics?.user()?.anonymousId()) {
      const anonymousId = window.analytics.user().anonymousId();
      return anonymousId;
    }
    
    const userId = window.localStorage.getItem('ajs_user_id');
    if (userId && userId !== 'null' && userId !== 'undefined') {
      return userId;
    }
    
    return null;
  };

  const handleTreatmentChange = useCallback((treatment: string) => {
    const key = getKey();
    handleSplitTreatment(FeatureFlag, treatment, key, trackEvent);
    setSplitTreatment(treatment);
  }, [FeatureFlag, trackEvent, setSplitTreatment]);

  useEffect(() => {
    if (!isSegmentReady) return;

    if (splitClientRef.current) return;

    const key = getKey();

    if (!factory) {
      factory = SplitFactory({
        core: {
          authorizationKey: SPLIT_API_KEY,
          key: `${key}`,
        } as SplitIO.IBrowserSettings['core'],
        startup: {
          readyTimeout: 1.5,
        },
      });
    }

    if (!client) {
      client = factory.client();
    }

    if (client) {
      client.on(client.Event.SDK_READY, () => {
        const treatment = client.getTreatment(FeatureFlag);
        if (treatment) {
          handleTreatmentChange(treatment);
        }
        splitClientRef.current = true;
      });

      client.on(client.Event.SDK_READY_TIMED_OUT, () => {
        handleTreatmentChange('control');
        splitClientRef.current = true;
      });
    }
  }, [isSegmentReady, FeatureFlag, handleTreatmentChange]);

  return {
    client,
    splitTreatment,
  };
};